import { useCallback, useMemo, useState } from "react";
import { format, formatISO, isValid } from "date-fns";
import type { ActivityOutcomeKind } from "@common/schemas/domains/activity/activity-outcomes.schema";
import {
    AuditLogChangeType,
    OrganizationHistoryDetailsQuery,
} from "@common/types/auditLog";
import { fullDateFormatWithTime } from "@common/helpers/date";
import { useOrganizationHistoryDetails } from "@api/organizationAuditLog.api";
import { useScope } from "@state/scopeContext.lib";
import { SubScopeProvider } from "@state/subScopeContext";
import { Drawer } from "@design-system/Drawer/Drawer";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import headerStyles from "@components/history/details/ActivityDrawerHeader.module.scss";
import historyDetailsStyles from "@components/history/details/HistoryDetails.module.scss";
import {
    HistoryTaskCreateDeleteDetails,
    HistoryTaskUpdateDetails,
} from "@components/history/details/task/HistoryTaskDetails";
import { buildTaskUpdateDetailsRow } from "@components/history/details/task/utils/historyTaskRows";
import { OrganizationActivityActor } from "../OrganizationActivityActor";
import {
    isOrganizationMemberActivity,
    OrganizationActivityRow,
} from "../organizationActivityRow";
import { describeOrganizationActivityAction } from "../describeOrganizationActivity";
import { OrganizationActivityChangesFooter } from "./OrganizationActivityChangesFooter";
import {
    OrganizationMemberCreateDeleteDetails,
    OrganizationMemberUpdateDetails,
} from "./OrganizationMemberActivityDetails";

const DETAILS_PAGE_SIZE = 20;

interface OrganizationActivityDetailsDrawerProps {
    row: OrganizationActivityRow | null;
    /** The list's outcome filter; the details then hold only the changes the row counted. */
    outcome?: ActivityOutcomeKind;
    open: boolean;
    onClose: () => void;
}

export const OrganizationActivityDetailsDrawer = ({
    row,
    outcome,
    open,
    onClose,
}: OrganizationActivityDetailsDrawerProps) => {
    const { organizationId } = useScope();
    const [pageState, setPageState] = useState({ rowId: row?.id, page: 1 });
    const page = pageState.rowId === row?.id ? pageState.page : 1;
    const handlePageChange = useCallback(
        (nextPage: number) => setPageState({ rowId: row?.id, page: nextPage }),
        [row?.id],
    );

    const detailsQuery = useMemo<OrganizationHistoryDetailsQuery | null>(
        () =>
            row
                ? {
                      date: row.date,
                      sourceType: row.sourceType,
                      resourceType: row.resourceType,
                      actorId: row.actorId,
                      entityIds: row.entityIds,
                      changeTypes: row.changeTypes,
                      fields: row.fields,
                      sourceIds: row.historyItem.sourceIds,
                      clientWorkspaceId: row.workspaceId,
                      outcome,
                      page,
                      limit: DETAILS_PAGE_SIZE,
                  }
                : null,
        [row, outcome, page],
    );
    const {
        data: details,
        isLoading,
        isError,
        refetch,
    } = useOrganizationHistoryDetails({
        organizationId: organizationId ?? "",
        query: detailsQuery,
    });
    const logs = useMemo(() => details?.data ?? [], [details]);
    const footer = (
        <OrganizationActivityChangesFooter
            total={details?.total ?? 0}
            page={page}
            pageCount={details?.totalPages ?? 1}
            onPageChange={handlePageChange}
        />
    );

    const taskCreateDeleteLogs = useMemo(
        () =>
            logs.filter(
                ({ changeType }) =>
                    changeType === AuditLogChangeType.CREATE ||
                    changeType === AuditLogChangeType.DELETE,
            ),
        [logs],
    );
    const taskUpdateData = useMemo(
        () =>
            logs
                .filter(
                    (log) =>
                        log.changeType === AuditLogChangeType.UPDATE &&
                        log.diff.length > 0,
                )
                .map(buildTaskUpdateDetailsRow),
        [logs],
    );

    const headerAddonLeft = useMemo(() => {
        if (!row) {
            return null;
        }

        const activityTitle = describeOrganizationActivityAction(row);
        const date = new Date(row.date);
        const hasUsableDate = isValid(date);

        return (
            <div className={headerStyles.root}>
                <OrganizationActivityActor
                    row={row}
                    nameClassName={headerStyles.actorName}
                />
                <span className={headerStyles.divider}>·</span>
                <h2 className={headerStyles.title} title={activityTitle}>
                    {activityTitle}
                </h2>
                {hasUsableDate && (
                    <>
                        <span className={headerStyles.divider}>·</span>
                        <time
                            dateTime={formatISO(date)}
                            className={headerStyles.date}
                        >
                            {format(date, fullDateFormatWithTime)}
                        </time>
                    </>
                )}
            </div>
        );
    }, [row]);

    const renderContent = (activityRow: OrganizationActivityRow) => {
        if (isLoading) {
            return <Loader />;
        }

        if (isError) {
            return (
                <EmptyState
                    header="Failed to load changes"
                    ctaText="Retry"
                    action={() => {
                        void refetch();
                    }}
                />
            );
        }

        const isUpdate = activityRow.changeTypes.includes(
            AuditLogChangeType.UPDATE,
        );

        if (isOrganizationMemberActivity(activityRow)) {
            return isUpdate ? (
                <OrganizationMemberUpdateDetails logs={logs} footer={footer} />
            ) : (
                <OrganizationMemberCreateDeleteDetails
                    logs={logs}
                    footer={footer}
                />
            );
        }

        return (
            <SubScopeProvider value={{ workspaceId: activityRow.workspaceId }}>
                {isUpdate ? (
                    <HistoryTaskUpdateDetails
                        data={taskUpdateData}
                        footer={footer}
                    />
                ) : (
                    <HistoryTaskCreateDeleteDetails
                        logs={taskCreateDeleteLogs}
                        footer={footer}
                    />
                )}
            </SubScopeProvider>
        );
    };

    return (
        <Drawer
            open={open}
            onClose={onClose}
            direction="right"
            headerAddonLeft={headerAddonLeft}
            size="half"
            noPadding
        >
            {row && (
                <div className={historyDetailsStyles.drawerContent}>
                    {renderContent(row)}
                </div>
            )}
        </Drawer>
    );
};
